import { useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { Banner } from './Banner';

/**
 * Which test this paper is part of.
 *
 * A test is set on a class as a whole, so a paper belonging to no test cannot
 * be given to anybody. Papers imported before that rule — and any imported
 * without a name typed into the box — need somewhere to be attached, and this
 * is it: type the name of the test and the paper joins it.
 *
 * The name is the whole of the grouping. Three papers carrying it are one
 * test; clearing it takes this one out again.
 */
export function SetNameField({
  testId,
  setName,
  known,
  onSaved,
}: {
  testId: string;
  setName: string | null;
  /** The tests this centre already has, to complete from and to avoid typos. */
  known: string[];
  onSaved: () => void;
}) {
  const [value, setValue] = useState(setName ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const changed = value.trim() !== (setName ?? '');

  async function save() {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const next = value.trim();
      await api.patch(`/tests/${testId}`, { setName: next });
      setNotice(
        next
          ? `This paper is part of ${next}.`
          : 'This paper belongs to no test, so it cannot be set on a class.',
      );
      onSaved();
    } catch (err) {
      setError(errorMessage(err, 'Could not change the test'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="panel stack">
      <div>
        <h2 className="panel__title">Part of which test?</h2>
        <p className="muted" style={{ maxWidth: '64ch' }}>
          Three papers sharing a name are one test, and a test is what gets set on a class — its
          Listening, its Reading and its Writing together. Type the name of the test this paper
          belongs to; the name is the whole of the grouping.
        </p>
      </div>

      {error && <Banner tone="error">{error}</Banner>}
      {notice && <Banner tone="success">{notice}</Banner>}

      <div className="row" style={{ gap: 'var(--s-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label className="field" style={{ maxWidth: '22rem', flex: '1 1 16rem' }}>
          <span className="field__label">Test name</span>
          <input
            className="field__input"
            list="known-tests"
            maxLength={120}
            placeholder="Test 1"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && changed) void save();
            }}
          />
          <datalist id="known-tests">
            {known.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
        </label>
        <button className="btn" disabled={saving || !changed} onClick={() => void save()}>
          {saving ? 'Saving…' : setName ? 'Change' : 'Add to this test'}
        </button>
      </div>

      {!setName && (
        <p className="field__hint">
          Until this paper is part of a test, nobody can be given it.
        </p>
      )}
    </section>
  );
}
